/**
 * Utility functions for parsing amount values with support for both comma and period as decimal separators
 */

/**
 * Parse amount string handling both comma and period as decimal separators
 * @param value - The input string to parse
 * @returns Parsed number or null if invalid
 */
export const parseAmount = (value: string): number | null => {
  if (value === '' || value === null || value === undefined) return null;
  
  // Convert to string if not already
  const stringValue = String(value).trim();
  
  // Handle empty string
  if (stringValue === '') return null;
  
  // Replace comma with period for parsing
  const normalizedValue = stringValue.replace(',', '.');
  
  // Check if the normalized value is a valid number
  const parsed = parseFloat(normalizedValue);
  if (isNaN(parsed)) return null;
  
  return parsed;
};

/**
 * Validate if a string represents a valid amount
 * @param value - The input string to validate
 * @returns True if valid, false otherwise
 */
export const isValidAmount = (value: string): boolean => {
  if (value === '' || value === null || value === undefined) return true; // Empty is valid (null)
  
  // Allow digits, one decimal separator (comma or period), and optional leading/trailing spaces
  const amountRegex = /^\s*\d*[.,]?\d*\s*$/;
  return amountRegex.test(value);
};

/**
 * Format amount for display with proper decimal places
 * @param value - The number to format
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted string
 */
export const formatAmount = (value: number | null | undefined, decimals: number = 2): string => {
  if (value === null || value === undefined) return '';
  return value.toFixed(decimals);
};

/**
 * Parse and validate amount input, returning both parsed value and validation status
 * @param value - The input string to parse
 * @returns Object with parsed value and validation status
 */
export const parseAndValidateAmount = (value: string): { 
  value: number | null; 
  isValid: boolean; 
  error?: string 
} => {
  if (value === '' || value === null || value === undefined) {
    return { value: null, isValid: true };
  }
  
  const stringValue = String(value).trim();
  
  if (stringValue === '') {
    return { value: null, isValid: true };
  }
  
  if (!isValidAmount(stringValue)) {
    return { 
      value: null, 
      isValid: false, 
      error: 'Invalid amount format. Use digits with . or , as decimal separator.' 
    };
  }
  
  const parsed = parseAmount(stringValue);
  
  if (parsed === null) {
    return { 
      value: null, 
      isValid: false, 
      error: 'Unable to parse amount.' 
    };
  }
  
  if (parsed < 0) {
    return { 
      value: null, 
      isValid: false, 
      error: 'Amount cannot be negative.' 
    };
  }
  
  return { value: parsed, isValid: true };
};